
import { useState, useCallback, useEffect, useRef } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import type { CarouselApi } from "@/components/ui/carousel";
import { useIsMobile } from "@/hooks/use-mobile";

const carouselImages = ["/lovable-uploads/fc29d2bd-75b7-4718-9971-d11d0fc59777.png", "/lovable-uploads/e64f6a4e-aa32-4105-976c-1d6d71e41453.png", "/lovable-uploads/c577f9da-620e-45e7-8bb9-1c7314b7862e.png", "/lovable-uploads/42df6479-9827-4762-825f-571963bd0ca4.png", "/lovable-uploads/b146e022-1fe8-40a4-861a-db11d4d1bc90.png", "/lovable-uploads/518d4b63-5e99-4d40-98bb-b416ea8abf02.png", "/lovable-uploads/58fb91ea-8e3f-4b08-a768-08c041caa5d5.png", "/lovable-uploads/cbccb3c0-442d-40a6-b837-d0d415e62ecc.png", "/lovable-uploads/b887d6c0-b819-4cb7-b371-23c323eb30e0.png"];
const carouselTitles = ["Akira", "Atlantis", "Big Girls", "Pirates", "Steeple", "Hunters", "Sétimos Filhos", "Southern Cross", "Cyber Punk"];

export const FeaturedCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const isMobile = useIsMobile();
  const pluginRef = useRef<ReturnType<typeof Autoplay> | null>(null);
  
  // Create plugin instance only once
  useEffect(() => {
    pluginRef.current = Autoplay({
      delay: 5000,
      stopOnInteraction: true
    });
  }, []);
  
  const onSelect = useCallback(() => {
    if (!api) return;
    setActiveIndex(api.selectedScrollSnap());
  }, [api]);
  
  useEffect(() => {
    if (!api) return;
    onSelect();
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, onSelect]);
  
  return (
    <div className="relative w-full overflow-hidden">
      <div className="container mx-auto px-2 sm:px-4">
        <Carousel 
          plugins={pluginRef.current ? [pluginRef.current] : []} 
          setApi={setApi} 
          onSelect={onSelect} 
          opts={{
            align: "center",
            loop: true
          }} 
          className="w-full pb-4 py-0 px-0"
        >
          <CarouselContent className="py-4">
            {carouselImages.map((image, index) => (
              <CarouselItem 
                key={index} 
                className={`
                  basis-[120px] sm:basis-[160px] md:basis-[180px] 
                  flex justify-center px-1 sm:px-2 
                  transition-all duration-300
                `}
              >
                <div 
                  className={`
                    relative transition-all duration-500 
                    h-[200px] sm:h-[240px] md:h-[260px]
                    ${activeIndex === index ? 'scale-110 z-30 opacity-100' : 
                      Math.abs(index - activeIndex) === 1 ? 'scale-90 z-20 opacity-90' : 
                      Math.abs(index - activeIndex) === 2 ? 'scale-75 z-10 opacity-75' : 
                      'scale-60 z-0 opacity-60'}
                  `}
                >
                  <div className="relative h-full overflow-hidden rounded-lg shadow-lg">
                    <img 
                      src={image} 
                      alt={carouselTitles[index] || `Featured manga ${index + 1}`} 
                      className="h-full w-full object-cover bg-gray-200" 
                    />
                    <div 
                      className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 transition-opacity duration-300 ${Math.abs(index - activeIndex) <= 1 ? 'opacity-100' : 'opacity-0'}`}
                    >
                      <h3 className="text-white text-center font-bold text-sm">
                        {carouselTitles[index] || `Featured Manga ${index + 1}`}
                      </h3>
                    </div>
                    
                    {/* Chapter number overlay for specific images */}
                    {(index === 2 || index === 7) && (
                      <div className="absolute top-2 right-2 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
                        {index === 2 ? '3' : '01'}
                      </div>
                    )}
                    
                    {/* New chapter badge */}
                    {(index === 0 || index === 3) && (
                      <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">
                        !
                      </div>
                    )}
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          
          <CarouselPrevious className="left-1 sm:left-2 w-7 h-7 sm:w-8 sm:h-8 bg-white/10 hover:bg-white/20 border-0 text-white" />
          <CarouselNext className="right-1 sm:right-2 w-7 h-7 sm:w-8 sm:h-8 bg-white/10 hover:bg-white/20 border-0 text-white" />
        </Carousel>
        
        {/* Pagination Dots */}
        <div className="flex justify-center gap-1 pb-2 px-0 mx-0 my-[4px] py-[3px]">
          {carouselImages.map((_, index) => (
            <button 
              key={index} 
              className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${activeIndex === index ? 'bg-primary scale-125' : 'bg-gray-400/30 hover:bg-gray-400/50'}`} 
              onClick={() => api?.scrollTo(index)} 
              aria-label={`Go to slide ${index + 1}`} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};
